import React, { useState, useMemo, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { Category, Resource } from './types';
import { normalizeCategoryFilterParam, normalizeResourceCategoryId } from './utils/category';
import { normalizeResourceSortParam, type ResourceSort } from './utils/resourceSort';
import { buildPaginationNumbers, RESOURCE_PAGE_SIZE } from './utils/pagination';
import { toPageSummary } from './utils/resourceListing';
import { toHttps } from './utils/secureUrl';
import { type SearchMode } from './utils/smartSearch';
import {
  CATEGORY_L1_VALUES,
  CATEGORY_L2_VALUES,
  normalizeCategoryL1,
  normalizeCategoryL2,
  type CategoryL1,
  type CategoryL2,
} from './server/categoryTaxonomy';
import Sidebar from './components/Sidebar';
import ResourceCard from './components/ResourceCard';
import MagicSearch, { MagicSearchRef } from './components/MagicSearch';
import LanguageToggle from './components/LanguageToggle';
import ThemeToggle from './components/ThemeToggle';
import UserMenu from './components/UserMenu';
import EffectsRuntime from './components/effects/EffectsRuntime';
import NoiseOverlay from './components/effects/NoiseOverlay';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MotionProvider } from './contexts/MotionContext';
import { useResources as useSupabaseResources, useCreateResource, useUpdateResource, useDeleteResource, useAdminDeleteResource } from './hooks/useResources';
import { useAccessibleResourceFacets, useAccessibleResources } from './hooks/useAccessibleResources';
import { useResourceFavorites, useToggleResourceFavorite } from './hooks/useResourceFavorites';
import { useMyTeams } from './hooks/useTeams';
import { useNotifications, useBroadcasts } from './hooks/useNotifications';
import { useNotificationPreferences } from './hooks/useNotificationPreferences';
import { useAnnouncementModal } from './hooks/useAnnouncementModal';
import { useSEO } from './hooks/useSEO';
import ScopeSelector, { ResourceScope } from './components/ScopeSelector';
import { supabase, isAdmin as checkIsAdmin } from './lib/supabase';
import { trackEvent } from './lib/analytics';
import { setSentryUser } from './lib/sentry';

// Lazy load components that are not immediately needed
const LandingPage = lazy(() => import('./components/LandingPage'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const SubmitResourceModal = lazy(() => import('./components/SubmitResourceModal'));
const SubmissionReview = lazy(() => import('./components/SubmissionReview'));
const ResourcePublishReview = lazy(() => import('./components/ResourcePublishReview'));
const MySubmissions = lazy(() => import('./components/MySubmissions'));
const MyResources = lazy(() => import('./components/MyResources'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const UserLogin = lazy(() => import('./components/UserLogin'));
const NotificationBell = lazy(() => import('./components/NotificationBell'));
const NotificationCenter = lazy(() => import('./components/NotificationCenter'));
const MessageCenter = lazy(() => import('./components/MessageCenter'));
const Banner = lazy(() => import('./components/Banner'));
const AnnouncementModal = lazy(() => import('./components/AnnouncementModal'));
const RepoInsightModal = lazy(() => import('./components/RepoInsightModal'));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      <span className="text-slate-500 dark:text-slate-400 text-sm">Loading...</span>
    </div>
  </div>
);

type L1Filter = 'all' | CategoryL1;
type L2Filter = 'all' | CategoryL2;
const MAX_VISIBLE_PAGE_BUTTONS = 7;

const CATEGORY_L1_LABELS: Record<CategoryL1, { zh: string; en: string }> = {
  models: { zh: '模型', en: 'Models' },
  apis: { zh: '接口', en: 'APIs' },
  agents: { zh: '智能体', en: 'Agents' },
  frameworks: { zh: '框架', en: 'Frameworks' },
  tools: { zh: '工具', en: 'Tools' },
  apps: { zh: '应用', en: 'Apps' },
  data: { zh: '数据', en: 'Data' },
  tutorials: { zh: '教程', en: 'Tutorials' },
  community: { zh: '社区', en: 'Community' },
  pending: { zh: '待定', en: 'Pending' },
};

const CATEGORY_L2_LABELS: Record<CategoryL2, { zh: string; en: string }> = {
  development: { zh: '开发', en: 'Development' },
  office: { zh: '办公', en: 'Office' },
  content_creation: { zh: '内容创作', en: 'Content Creation' },
  data_analysis: { zh: '数据分析', en: 'Data Analysis' },
  design: { zh: '设计', en: 'Design' },
  marketing: { zh: '营销', en: 'Marketing' },
  security: { zh: '安全', en: 'Security' },
  education: { zh: '教育', en: 'Education' },
};

function mapLegacyCategoryToL1(category: Category): L1Filter {
  switch (category) {
    case Category.ALL:
      return 'all';
    case Category.MODELS:
      return 'models';
    case Category.PLATFORMS:
      return 'tools';
    case Category.PROJECTS:
      return 'apps';
    case Category.CONTENT:
      return 'tutorials';
    case Category.COMMUNITY:
      return 'community';
    case Category.OTHER:
    default:
      return 'pending';
  }
}

const AppContent: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  useSEO();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Page state with URL persistence (landing vs main)
  const [showLanding, setShowLanding] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      // If URL has any params (category, page=main, etc.), show main page
      if (
        params.has('category')
        || params.has('l1')
        || params.has('l2')
        || params.has('tag')
        || params.has('tags')
        || params.get('page') === 'main'
      ) {
        return false;
      }
    }
    return true;
  });

  // Sync page state to URL (preserve hash for Supabase auth callback)
  const handleEnterMain = useCallback(() => {
    trackEvent('enter_main', { language });
    setShowLanding(false);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 'main');
    const hash = window.location.hash;
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${hash}`);
  }, [language]);

  // Return to landing page (preserve hash for Supabase auth callback)
  const handleReturnToLanding = useCallback(() => {
    setShowLanding(true);
    setMobileMenuOpen(false);
    setActiveL1('all');
    setActiveL2('all');
    setActiveTags([]);
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    const nextParams = new URLSearchParams();
    if (lang) nextParams.set('lang', lang);
    const newUrl = nextParams.toString()
      ? `${window.location.pathname}?${nextParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', `${newUrl}${hash}`);
  }, []);

  // L1 category state with URL persistence
  const [activeL1, setActiveL1] = useState<L1Filter>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const l1Param = params.get('l1');
      if (l1Param) {
        return l1Param === 'all' ? 'all' : normalizeCategoryL1(l1Param);
      }

      const categoryParam = params.get('category');
      if (categoryParam) {
        return mapLegacyCategoryToL1(normalizeCategoryFilterParam(categoryParam));
      }
    }
    return 'all';
  });

  // L2 category state with URL persistence
  const [activeL2, setActiveL2] = useState<L2Filter>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const l2Param = params.get('l2');
      if (l2Param) {
        return l2Param === 'all' ? 'all' : normalizeCategoryL2(l2Param);
      }
    }
    return 'all';
  });

  // Tag filter state with URL persistence (multi-select)
  const [activeTags, setActiveTags] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tagsParam = params.get('tags');
      if (tagsParam) {
        return tagsParam
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
          .slice(0, 12);
      }

      const legacyTag = params.get('tag');
      if (legacyTag) return [legacyTag.trim()].filter(Boolean);
    }
    return [];
  });

  // Sync l1/l2/tags to URL (preserve hash for Supabase auth callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeL1 === 'all') {
      params.delete('l1');
    } else {
      params.set('l1', activeL1);
    }
    if (activeL2 === 'all') {
      params.delete('l2');
    } else {
      params.set('l2', activeL2);
    }

    if (activeTags.length > 0) {
      params.set('tags', activeTags.join(','));
    } else {
      params.delete('tags');
    }

    params.delete('category');
    params.delete('tag');

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    const hash = window.location.hash;
    window.history.replaceState({}, '', `${newUrl}${hash}`);
  }, [activeL1, activeL2, activeTags]);

  const [basicSearch, setBasicSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('q') || params.get('search');
      if (q) return q.trim().slice(0, 200);
    }
    return '';
  });
  const [searchMode, setSearchMode] = useState<SearchMode>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('search_mode');
      if (raw === 'normal' || raw === 'smart') {
        return raw;
      }
    }
    return 'smart';
  });
  const searchRef = useRef<MagicSearchRef>(null);

  // Scope filter state with URL persistence
  const [scope, setScope] = useState<ResourceScope>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const scopeParam = params.get('scope');
      if (scopeParam && ['all', 'public', 'team', 'private'].includes(scopeParam)) {
        return scopeParam as ResourceScope;
      }
    }
    return 'public';
  });

  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('favorites') === '1';
    }
    return false;
  });

  const [featuredOnly, setFeaturedOnly] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('featured') === '1';
    }
    return false;
  });

  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('teamId') || undefined;
    }
    return undefined;
  });

  const [sort, setSort] = useState<ResourceSort>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return normalizeResourceSortParam(params.get('sort'));
    }
    return 'latest';
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Sync scope and teamId to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (scope && scope !== 'all') {
      params.set('scope', scope);
    } else {
      params.delete('scope');
    }
    if (selectedTeamId) {
      params.set('teamId', selectedTeamId);
    } else {
      params.delete('teamId');
    }
    if (favoritesOnly) {
      params.set('favorites', '1');
    } else {
      params.delete('favorites');
    }
    if (featuredOnly) {
      params.set('featured', '1');
    } else {
      params.delete('featured');
    }
    if (sort !== 'latest') {
      params.set('sort', sort);
    } else {
      params.delete('sort');
    }
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    const hash = window.location.hash;
    window.history.replaceState({}, '', `${newUrl}${hash}`);
  }, [scope, selectedTeamId, favoritesOnly, featuredOnly, sort]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (searchMode === 'smart') {
      params.delete('search_mode');
    } else {
      params.set('search_mode', searchMode);
    }
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    const hash = window.location.hash;
    window.history.replaceState({}, '', `${newUrl}${hash}`);
  }, [searchMode]);

  // Handle tag click from ResourceCard (multi-select)
  const handleTagClick = useCallback((tag: string) => {
    setActiveTags((prev) => {
      const exists = prev.some((value) => value.toLowerCase() === tag.toLowerCase());
      if (exists) {
        return prev.filter((value) => value.toLowerCase() !== tag.toLowerCase());
      }
      return [...prev, tag].slice(0, 12);
    });
  }, []);

  // Keyboard shortcut: Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close mobile menu
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        return;
      }
      // Cmd/Ctrl+K to focus search
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        const activeEl = document.activeElement;
        const isInInput = activeEl instanceof HTMLInputElement ||
                          activeEl instanceof HTMLTextAreaElement ||
                          (activeEl as HTMLElement)?.isContentEditable;
        if (isInInput && activeEl !== searchRef.current) {
          return;
        }
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  // User Auth State
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let currentUserId: string | null = null;

    const checkAdmin = async (userId: string) => {
      const result = await checkIsAdmin(userId);
      if (isMounted && userId === currentUserId) {
        setIsAdmin(result);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      const sessionUser = session?.user ?? null;
      currentUserId = sessionUser?.id ?? null;
      setUser(sessionUser);
      setSentryUser(sessionUser ? { id: sessionUser.id } : null);
      if (sessionUser) {
        checkAdmin(sessionUser.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user ?? null;
      currentUserId = sessionUser?.id ?? null;
      setUser(sessionUser);
      setSentryUser(sessionUser ? { id: sessionUser.id } : null);
      if (sessionUser) {
        checkAdmin(sessionUser.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Fetch user's teams for ScopeSelector
  const { data: userTeams } = useMyTeams(user?.id);
  const teamsForSelector = useMemo(() => {
    return userTeams?.map(t => ({ id: t.id, name: t.name })) || [];
  }, [userTeams]);

  // Handle scope change
  const handleScopeChange = useCallback((newScope: ResourceScope, teamId?: string) => {
    setScope(newScope);
    setSelectedTeamId(teamId);
  }, []);

  const {
    data: accessibleResourcePage,
    isLoading: isResourcesLoading,
  } = useAccessibleResources(
    user?.id,
    {
      scope,
      teamId: selectedTeamId,
      category: Category.ALL,
      categoryL1: activeL1,
      categoryL2: activeL2,
      tags: activeTags.length > 0 ? activeTags : undefined,
      search: basicSearch,
      searchMode,
      sort: (scope === 'public' || scope === 'all') ? sort : 'latest',
      page: currentPage,
      pageSize: RESOURCE_PAGE_SIZE,
      favoritesOnly: Boolean(user && favoritesOnly),
      featuredOnly,
    },
    { enabled: !showLanding },
  );

  const { data: resourceFacets } = useAccessibleResourceFacets(
    user?.id,
    {
      scope,
      teamId: selectedTeamId,
      category: Category.ALL,
      search: basicSearch,
      searchMode,
      favoritesOnly: Boolean(user && favoritesOnly),
      featuredOnly,
      activeL1,
      activeL2,
    },
    { enabled: !showLanding },
  );

  // Admin 面板需要完整资源集：仅管理员且进入主站后加载
  const { data: supabaseResources, isLoading: isAdminResourcesLoading } = useSupabaseResources(undefined, {
    enabled: isAdmin && !showLanding,
  });

  // Mutation hooks for CRUD operations
  const createResourceMutation = useCreateResource();
  const updateResourceMutation = useUpdateResource();
  const deleteResourceMutation = useDeleteResource();
  const adminDeleteResourceMutation = useAdminDeleteResource();

  // Use Supabase data directly, no fallback to local resources
  // Empty array is a valid state (user deleted all resources)
  const resources = useMemo(() => {
    if (isResourcesLoading || !accessibleResourcePage) {
      return [];
    }
    return accessibleResourcePage.items.map((r: any) => ({
      ...r,
      category: normalizeResourceCategoryId(r.category),
      icon: r.icon || undefined,
      featured: r.featured || false,
      team_name: r.team_name || r.teams?.name || undefined,
    })) as Resource[];
  }, [accessibleResourcePage, isResourcesLoading]);

  const totalResources = accessibleResourcePage?.total ?? 0;
  const currentPageResources = resources;

  const resourceIds = useMemo(() => resources.map(r => r.id), [resources]);
  const { data: favoriteIds = [] } = useResourceFavorites(user?.id, resourceIds);
  const favoriteIdSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);
  const toggleFavoriteMutation = useToggleResourceFavorite(user?.id);

  const handleToggleFavorite = useCallback((resourceId: string, next: boolean) => {
    toggleFavoriteMutation.mutate({ resourceId, next });
  }, [toggleFavoriteMutation]);

  const handleSortChange = useCallback((next: ResourceSort) => {
    setSort(prev => {
      if (prev === next) return prev;
      trackEvent('sort_change', {
        sort: next,
        scope,
        category_l1: activeL1,
        category_l2: activeL2,
        tags_count: activeTags.length,
        search_length: Math.min(basicSearch.trim().length, 200),
      });
      return next;
    });
  }, [activeL1, activeL2, activeTags.length, basicSearch, scope]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeL1, activeL2, activeTags, basicSearch, favoritesOnly, featuredOnly, scope, searchMode, selectedTeamId, sort]);

  const pageSummary = useMemo(() => (
    toPageSummary({ page: currentPage, pageSize: RESOURCE_PAGE_SIZE, total: totalResources })
  ), [currentPage, totalResources]);
  const safeCurrentPage = pageSummary.currentPage;
  const totalPages = pageSummary.totalPages;

  useEffect(() => {
    if (safeCurrentPage !== currentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  const paginationButtons = useMemo(() => (
    buildPaginationNumbers(safeCurrentPage, totalPages, MAX_VISIBLE_PAGE_BUTTONS)
  ), [safeCurrentPage, totalPages]);

  const pageStart = pageSummary.pageStart;
  const pageEnd = pageSummary.pageEnd;

  const handlePageChange = useCallback((nextPage: number) => {
    setCurrentPage(nextPage);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const emptyL1Counts = useMemo(() => (
    CATEGORY_L1_VALUES.reduce((acc, value) => {
      acc[value] = 0;
      return acc;
    }, {} as Record<CategoryL1, number>)
  ), []);
  const emptyL2Counts = useMemo(() => (
    CATEGORY_L2_VALUES.reduce((acc, value) => {
      acc[value] = 0;
      return acc;
    }, {} as Record<CategoryL2, number>)
  ), []);
  const l1Counts = resourceFacets?.l1Counts ?? emptyL1Counts;
  const l2Counts = resourceFacets?.l2Counts ?? emptyL2Counts;
  const availableTags = resourceFacets?.availableTags ?? [];

  const adminResources = useMemo(() => {
    if (!isAdmin || isAdminResourcesLoading || !supabaseResources) return [];
    return supabaseResources.map(r => ({
      ...r,
      category: normalizeResourceCategoryId(r.category),
      icon: r.icon || undefined,
      featured: r.featured || false,
    })) as Resource[];
  }, [isAdmin, isAdminResourcesLoading, supabaseResources]);

  // Admin Panel State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const [showPublishReviewPanel, setShowPublishReviewPanel] = useState(false);
  const [showMySubmissions, setShowMySubmissions] = useState(false);
  const [showMyResources, setShowMyResources] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showNotificationCenterDesktop, setShowNotificationCenterDesktop] = useState(false);
  const [showNotificationCenterMobile, setShowNotificationCenterMobile] = useState(false);
  const [showMessageCenter, setShowMessageCenter] = useState(false);
  const [messageCenterInitialId, setMessageCenterInitialId] = useState<string | null>(null);
  const [showRepoInsightModal, setShowRepoInsightModal] = useState(false);
  const [repoInsightResource, setRepoInsightResource] = useState<Resource | null>(null);

  // Notifications
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    archiveNotifications,
    unarchiveNotifications,
    deleteNotifications,
    isMarkingAllAsRead,
    hasNextPage: hasMoreNotifications,
    isFetchingNextPage: isFetchingMoreNotifications,
    fetchNextPage: fetchMoreNotifications,
    isLoading: isNotificationsLoading,
    error: notificationsError,
    refetch: refetchNotifications,
    isRefetching: isRefetchingNotifications,
  } = useNotifications(user?.id);

  const {
    preferences: notificationPreferences,
    isLoading: isNotificationPreferencesLoading,
    error: notificationPreferencesError,
    savePreferences: saveNotificationPreferences,
    isSaving: isSavingNotificationPreferences,
  } = useNotificationPreferences(user?.id);

  // Broadcasts (site-wide announcements)
  const {
    activeBanners,
    messageCenter,
    dismissBroadcast,
    getLocalizedContent,
    trackBroadcastEvent,
  } = useBroadcasts(user?.id, language);

  // Announcement Modal (auto-popup for modal type broadcasts)
  const {
    isOpen: isAnnouncementModalOpen,
    unreadAnnouncements,
    close: closeAnnouncementModal,
    markAsRead: markAnnouncementsAsRead,
    refresh: refreshAnnouncements,
  } = useAnnouncementModal(user?.id, language);

  // Broadcast view tracking
  useEffect(() => {
    if (!activeBanners || activeBanners.length === 0) return;
    const topBanner = activeBanners.reduce((highest, current) =>
      current.priority > highest.priority ? current : highest
    );
    trackBroadcastEvent(topBanner.id, 'view', { source: 'banner' });
  }, [activeBanners, trackBroadcastEvent]);

  useEffect(() => {
    if (!showMessageCenter) return;
    if (!messageCenter || messageCenter.length === 0) return;
    messageCenter.forEach(b => {
      trackBroadcastEvent(b.id, 'view', { source: 'message_center' });
    });
  }, [showMessageCenter, messageCenter, trackBroadcastEvent]);

  // User Menu Actions
  const handleLogin = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  const handleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    setShowAdminPanel(false);
  }, []);

  const handleAdminPanel = useCallback(() => {
    if (!user) {
      setShowAdminLoginModal(true);
    } else {
      setShowAdminPanel(true);
    }
  }, [user]);

  const handleSubmitResource = useCallback(() => {
    setShowSubmitModal(true);
  }, []);

  const handleOpenRepoInsight = useCallback((resource: Resource) => {
    setRepoInsightResource(resource);
    setShowRepoInsightModal(true);
  }, []);

  const handleReviewSubmissions = useCallback(() => {
    setShowReviewPanel(true);
  }, []);

  const handleReviewPublishRequests = useCallback(() => {
    setShowPublishReviewPanel(true);
  }, []);

  const handleMySubmissions = useCallback(() => {
    setShowMySubmissions(true);
  }, []);

  const handleMyResources = useCallback(() => {
    setShowMyResources(true);
  }, []);

  const handleMyTeams = useCallback(() => {
    setShowTeamManagement(true);
  }, []);

  const handleOpenResourceLink = useCallback(async (resourceId: string) => {
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('url')
        .eq('id', resourceId)
        .maybeSingle();

      if (error || !data?.url) return;

      const url = toHttps(data.url) || data.url;
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) return;
      window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
    } catch {
      // ignore
    }
  }, []);

  const handleOpenMessageCenter = useCallback((notificationId?: string) => {
    setMessageCenterInitialId(notificationId || null);
    setShowMessageCenter(true);
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setShowAdminPanel(true);
  }, []);

  // Save resource: create or update in Supabase
  const handleSaveResource = useCallback(async (resource: Resource) => {
    const isExisting = supabaseResources?.some(r => r.id === resource.id);

    if (isExisting) {
      // Update existing resource
      updateResourceMutation.mutate({
        id: resource.id,
        updates: {
          title: resource.title,
          title_zh: resource.title_zh,
          description: resource.description,
          description_zh: resource.description_zh,
          url: resource.url,
          category: resource.category,
          category_l1: resource.category_l1,
          category_l2: resource.category_l2,
          category_confidence: resource.category_confidence,
          category_source: resource.category_source,
          icon: resource.icon,
          tags: resource.tags,
          featured: resource.featured,
        }
      });
    } else {
      // Create new resource
      createResourceMutation.mutate({
        title: resource.title,
        title_zh: resource.title_zh,
        description: resource.description,
        description_zh: resource.description_zh,
        url: resource.url,
        category: resource.category,
        category_l1: resource.category_l1,
        category_l2: resource.category_l2,
        category_confidence: resource.category_confidence,
        category_source: resource.category_source,
        icon: resource.icon,
        tags: resource.tags,
        featured: resource.featured,
      });
    }
  }, [supabaseResources, createResourceMutation, updateResourceMutation]);

  // Delete resource: soft delete in Supabase
  const handleDeleteResource = useCallback((id: string) => {
    if (isAdmin) {
      adminDeleteResourceMutation.mutate(id);
      return;
    }
    deleteResourceMutation.mutate(id);
  }, [adminDeleteResourceMutation, deleteResourceMutation, isAdmin]);

  if (showLanding) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LandingPage onEnter={handleEnterMain} />
      </Suspense>
    );
  }

  return (
    <div className="relative min-h-screen bg-background text-slate-800 dark:text-slate-200 flex font-sans animate-[fadeInUp_0.5s_ease-out]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg focus:outline-none"
      >
        {t.skip_to_content}
      </a>
      <Sidebar
        activeL1={activeL1}
        onSelectL1={setActiveL1}
        counts={l1Counts}
        isOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
        onClickTitle={handleReturnToLanding}
      />

      {showLoginModal && (
        <Suspense fallback={
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        }>
          <UserLogin
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
          />
        </Suspense>
      )}

      {showAdminLoginModal && (
        <Suspense fallback={null}>
          <AdminLogin
            isOpen={showAdminLoginModal}
            onClose={() => setShowAdminLoginModal(false)}
            onLogin={handleLoginSuccess}
          />
        </Suspense>
      )}

      {showAdminPanel && isAdmin && (
        <Suspense fallback={null}>
          <AdminPanel
            isOpen={showAdminPanel}
            onClose={() => setShowAdminPanel(false)}
            resources={adminResources}
            isLoading={isAdminResourcesLoading}
            onSave={handleSaveResource}
            onDelete={handleDeleteResource}
            onLogout={handleLogout}
          />
        </Suspense>
      )}

      {user && showSubmitModal && (
        <Suspense fallback={null}>
          <SubmitResourceModal
            isOpen={showSubmitModal}
            onClose={() => setShowSubmitModal(false)}
          />
        </Suspense>
      )}

      {isAdmin && showReviewPanel && (
        <Suspense fallback={null}>
          <SubmissionReview
            isOpen={showReviewPanel}
            onClose={() => setShowReviewPanel(false)}
          />
        </Suspense>
      )}

      {isAdmin && showPublishReviewPanel && (
        <Suspense fallback={null}>
          <ResourcePublishReview
            isOpen={showPublishReviewPanel}
            onClose={() => setShowPublishReviewPanel(false)}
          />
        </Suspense>
      )}

      {user && showMySubmissions && (
        <Suspense fallback={null}>
          <MySubmissions
            isOpen={showMySubmissions}
            onClose={() => setShowMySubmissions(false)}
            userId={user.id}
          />
        </Suspense>
      )}

      {user && showMyResources && (
        <Suspense fallback={<LoadingFallback />}>
          <MyResources
            isOpen={showMyResources}
            onClose={() => setShowMyResources(false)}
            userId={user.id}
          />
        </Suspense>
      )}

      {user && showTeamManagement && (
        <Suspense fallback={<LoadingFallback />}>
          <TeamManagement
            isOpen={showTeamManagement}
            onClose={() => setShowTeamManagement(false)}
            userId={user.id}
          />
        </Suspense>
      )}

      {user && showMessageCenter && (
        <Suspense fallback={<LoadingFallback />}>
          <MessageCenter
            isOpen={showMessageCenter}
            onClose={() => {
              setShowMessageCenter(false);
              setMessageCenterInitialId(null);
            }}
            notifications={notifications}
            announcements={messageCenter}
            onDismissAnnouncement={dismissBroadcast}
            getLocalizedContent={getLocalizedContent}
            hasNextPage={hasMoreNotifications}
            isFetchingNextPage={isFetchingMoreNotifications}
            onFetchNextPage={() => { fetchMoreNotifications() }}
            onOpenResource={handleOpenResourceLink}
            onOpenTeamManagement={handleMyTeams}
            isLoadingNotifications={isNotificationsLoading || isRefetchingNotifications}
            notificationsError={notificationsError}
            onRefreshNotifications={() => { refetchNotifications() }}
            onMarkAsRead={markAsRead}
            onMarkAsUnread={markAsUnread}
            onMarkAllAsRead={markAllAsRead}
            onArchive={archiveNotifications}
            onUnarchive={unarchiveNotifications}
            onDelete={deleteNotifications}
            isMarkingAllAsRead={isMarkingAllAsRead}
            notificationPreferences={notificationPreferences}
            isLoadingNotificationPreferences={isNotificationPreferencesLoading}
            notificationPreferencesError={notificationPreferencesError}
            onSaveNotificationPreferences={saveNotificationPreferences}
            isSavingNotificationPreferences={isSavingNotificationPreferences}
            onTrackBroadcastEvent={trackBroadcastEvent}
            language={language}
            initialSelectedId={messageCenterInitialId}
          />
        </Suspense>
      )}

      {showRepoInsightModal && repoInsightResource && (
        <Suspense fallback={null}>
          <RepoInsightModal
            isOpen={showRepoInsightModal}
            resource={repoInsightResource}
            onClose={() => {
              setShowRepoInsightModal(false);
              setRepoInsightResource(null);
            }}
          />
        </Suspense>
      )}

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 relative perspective-1000">

        {/* Site-wide Banner Announcements */}
        {activeBanners.length > 0 && (
          <Suspense fallback={null}>
            <Banner
              banners={activeBanners}
              onDismiss={dismissBroadcast}
              getLocalizedContent={getLocalizedContent}
            />
          </Suspense>
        )}

        {/* Modal Announcements */}
        {isAnnouncementModalOpen && unreadAnnouncements.length > 0 && (
          <Suspense fallback={null}>
            <AnnouncementModal
              isOpen={isAnnouncementModalOpen}
              announcements={unreadAnnouncements}
              onClose={closeAnnouncementModal}
              onMarkAsRead={markAnnouncementsAsRead}
              onRefresh={refreshAnnouncements}
              getLocalizedContent={getLocalizedContent}
              language={language}
            />
          </Suspense>
        )}

        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-slate-200 dark:border-white/5 p-4 flex justify-between items-center transition-colors duration-300">
          <button
            type="button"
            onClick={handleReturnToLanding}
            aria-label={language === 'zh' ? '返回首页' : 'Back to landing page'}
            className="text-xl font-bold text-slate-900 dark:text-white tracking-tight hover:opacity-80 transition-opacity"
          >
            {t.app_name}
          </button>
          <div className="flex items-center gap-2">
             <ThemeToggle />
             <button
               onClick={() => setLanguage(language === 'en' ? 'zh' : 'en')}
               className="text-xs font-bold px-3 py-1.5 bg-surface rounded-full border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300"
             >
               {language === 'en' ? '中文' : 'EN'}
             </button>
             {user && (
               <div className="relative">
                 <Suspense fallback={null}>
                   <NotificationBell
                     unreadCount={unreadCount}
                     onClick={() => setShowNotificationCenterMobile(prev => !prev)}
                   />
                   <NotificationCenter
                     isOpen={showNotificationCenterMobile}
                     onClose={() => setShowNotificationCenterMobile(false)}
                     notifications={notifications}
                     onMarkAsRead={markAsRead}
                     onMarkAllAsRead={markAllAsRead}
                     isMarkingAllAsRead={isMarkingAllAsRead}
                     isLoading={isNotificationsLoading || isRefetchingNotifications}
                     error={notificationsError}
                     onRefresh={() => { refetchNotifications() }}
                     language={language}
                     announcements={messageCenter}
                     onDismissAnnouncement={dismissBroadcast}
                     getLocalizedContent={getLocalizedContent}
                     onOpenMessageCenter={handleOpenMessageCenter}
                   />
                 </Suspense>
               </div>
             )}
             <UserMenu
               user={user}
               isAdmin={isAdmin}
               onLogin={handleLogin}
               onLogout={handleLogout}
               onAdminPanel={handleAdminPanel}
               onSubmitResource={user ? handleSubmitResource : undefined}
               onReviewSubmissions={isAdmin ? handleReviewSubmissions : undefined}
               onReviewPublishRequests={isAdmin ? handleReviewPublishRequests : undefined}
               onMySubmissions={user ? handleMySubmissions : undefined}
               onMyResources={user ? handleMyResources : undefined}
               onMyTeams={user ? handleMyTeams : undefined}
               onMessageCenter={user ? () => handleOpenMessageCenter() : undefined}
             />
             <button
               type="button"
               onClick={() => setMobileMenuOpen(true)}
               aria-label={language === 'zh' ? '打开菜单' : 'Open menu'}
               aria-expanded={mobileMenuOpen}
               aria-controls="app-sidebar"
               className="p-2 text-slate-500 dark:text-slate-300 hover:text-primary dark:hover:text-white"
             >
               <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
               </svg>
             </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">

          {/* Main Search Area + Desktop Top Controls */}
          <div className="mb-4 md:mb-6 lg:mb-5 flex items-center gap-3 lg:gap-4">
            <MagicSearch
              ref={searchRef}
              value={basicSearch}
              onChange={setBasicSearch}
              containerClassName="mb-0 max-w-none flex-1"
            />

            <div
              role="group"
              aria-label={language === 'zh' ? '检索模式' : 'Search mode'}
              className="shrink-0 flex items-center gap-1 p-1 rounded-full bg-surface border border-slate-200 dark:border-white/10 shadow-sm"
            >
              <button
                type="button"
                onClick={() => setSearchMode('normal')}
                aria-pressed={searchMode === 'normal'}
                className={`px-2.5 py-1 rounded-full text-xs md:text-sm font-medium transition-colors ${
                  searchMode === 'normal'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >
                {language === 'zh' ? '普通' : 'Normal'}
              </button>
              <button
                type="button"
                onClick={() => setSearchMode('smart')}
                aria-pressed={searchMode === 'smart'}
                className={`px-2.5 py-1 rounded-full text-xs md:text-sm font-medium transition-colors ${
                  searchMode === 'smart'
                    ? 'bg-primary/10 text-primary'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >
                {language === 'zh' ? '智能' : 'Smart'}
              </button>
            </div>

            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <ThemeToggle />
              <LanguageToggle />
              {user && (
                <div className="relative">
                  <Suspense fallback={null}>
                    <NotificationBell
                      unreadCount={unreadCount}
                      onClick={() => setShowNotificationCenterDesktop(prev => !prev)}
                    />
                    <NotificationCenter
                      isOpen={showNotificationCenterDesktop}
                      onClose={() => setShowNotificationCenterDesktop(false)}
                      notifications={notifications}
                      onMarkAsRead={markAsRead}
                      onMarkAllAsRead={markAllAsRead}
                      isMarkingAllAsRead={isMarkingAllAsRead}
                      isLoading={isNotificationsLoading || isRefetchingNotifications}
                      error={notificationsError}
                      onRefresh={() => { refetchNotifications() }}
                      language={language}
                      announcements={messageCenter}
                      onDismissAnnouncement={dismissBroadcast}
                      getLocalizedContent={getLocalizedContent}
                      onOpenMessageCenter={handleOpenMessageCenter}
                    />
                  </Suspense>
                </div>
              )}
              <UserMenu
                user={user}
                isAdmin={isAdmin}
                onLogin={handleLogin}
                onLogout={handleLogout}
                onAdminPanel={handleAdminPanel}
                onSubmitResource={user ? handleSubmitResource : undefined}
                onReviewSubmissions={isAdmin ? handleReviewSubmissions : undefined}
                onReviewPublishRequests={isAdmin ? handleReviewPublishRequests : undefined}
                onMySubmissions={user ? handleMySubmissions : undefined}
                onMyResources={user ? handleMyResources : undefined}
                onMyTeams={user ? handleMyTeams : undefined}
                onMessageCenter={user ? () => handleOpenMessageCenter() : undefined}
              />
            </div>
          </div>

          {/* Mobile L1 Tabs */}
          <div className="md:hidden -mx-4 px-4 mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setActiveL1('all')}
                aria-pressed={activeL1 === 'all'}
                className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                  activeL1 === 'all'
                    ? 'bg-primary/10 text-primary border-primary/20'
                    : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                }`}
              >
                <span>{language === 'zh' ? '全部' : 'All'}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                  activeL1 === 'all' ? 'bg-primary text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                }`}>
                  {totalResources}
                </span>
              </button>
              {CATEGORY_L1_VALUES.map((l1) => (
                <button
                  key={l1}
                  type="button"
                  onClick={() => setActiveL1(l1)}
                  aria-pressed={activeL1 === l1}
                  className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                    activeL1 === l1
                      ? 'bg-primary/10 text-primary border-primary/20'
                      : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                  }`}
                >
                  <span>{language === 'zh' ? CATEGORY_L1_LABELS[l1].zh : CATEGORY_L1_LABELS[l1].en}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                    activeL1 === l1 ? 'bg-primary text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                  }`}>
                    {l1Counts[l1] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* List Header & Counts */}
          <div className="mb-5 md:mb-7 space-y-3.5 md:space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-4 min-w-0">
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {activeL1 === 'all'
                    ? (language === 'zh' ? '全部资源' : 'All Resources')
                    : (language === 'zh' ? CATEGORY_L1_LABELS[activeL1].zh : CATEGORY_L1_LABELS[activeL1].en)}
                </h3>
                <span className="px-2.5 py-0.5 rounded-full bg-surface border border-slate-200 dark:border-white/10 text-xs font-medium text-slate-500 dark:text-slate-400 shadow-sm">
                  {totalResources} {t.items_found}
                </span>
              </div>

              <div className="hidden md:flex items-center gap-2 shrink-0">
                {(scope === 'public' || scope === 'all') && (
                  <div
                    role="group"
                    aria-label={t.sort_by}
                    className="flex items-center gap-1 p-1 rounded-full bg-surface border border-slate-200 dark:border-white/10 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() => handleSortChange('latest')}
                      aria-pressed={sort === 'latest'}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        sort === 'latest'
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      {t.sort_latest}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSortChange('most_favorited')}
                      aria-pressed={sort === 'most_favorited'}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        sort === 'most_favorited'
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      {t.sort_most_favorited}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setFeaturedOnly(prev => !prev)}
                  aria-pressed={featuredOnly}
                  aria-label={language === 'zh' ? '只看精选' : 'Featured only'}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    featuredOnly
                      ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                      : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill={featuredOnly ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.98 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.556l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                    />
                  </svg>
                  <span>{language === 'zh' ? '只看精选' : 'Featured'}</span>
                </button>
                {user && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFavoritesOnly(prev => !prev)}
                      aria-pressed={favoritesOnly}
                      aria-label={language === 'zh' ? '只看收藏' : 'Favorites only'}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                        favoritesOnly
                          ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                          : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill={favoritesOnly ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                      <span>{language === 'zh' ? '只看收藏' : 'Favorites'}</span>
                    </button>
                    <div className="shrink-0">
                      <ScopeSelector
                        scope={scope}
                        onScopeChange={handleScopeChange}
                        teams={teamsForSelector}
                        selectedTeamId={selectedTeamId}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Desktop: stacked rows to avoid horizontal squeeze */}
            <div className="hidden md:flex flex-col gap-2.5 min-w-0">
              {(activeL1 !== 'all' || activeL2 !== 'all') && (
                <div className="w-full min-w-0 flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
                  <button
                    type="button"
                    onClick={() => setActiveL2('all')}
                    aria-pressed={activeL2 === 'all'}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      activeL2 === 'all'
                        ? 'bg-primary/10 text-primary border-primary/20'
                        : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                    }`}
                  >
                    {language === 'zh' ? '全部二级' : 'All L2'}
                  </button>
                  {CATEGORY_L2_VALUES.map((l2) => (
                    <button
                      key={l2}
                      type="button"
                      onClick={() => setActiveL2(l2)}
                      aria-pressed={activeL2 === l2}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        activeL2 === l2
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                      }`}
                    >
                      {language === 'zh' ? CATEGORY_L2_LABELS[l2].zh : CATEGORY_L2_LABELS[l2].en}
                      <span className="ml-1 text-xs opacity-80">{l2Counts[l2] || 0}</span>
                    </button>
                  ))}
                </div>
              )}

              {availableTags.length > 0 && (
                <div className="w-full min-w-0 flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
                  {availableTags.map(([tag, count]) => {
                    const isActive = activeTags.some((value) => value.toLowerCase() === tag.toLowerCase());
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagClick(tag)}
                        aria-pressed={isActive}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                        }`}
                      >
                        #{tag}
                        <span className="ml-1 text-xs opacity-80">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mobile: two-line layout with scrollable filters */}
            <div className="md:hidden w-full space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {activeL1 === 'all'
                    ? (language === 'zh' ? '全部资源' : 'All Resources')
                    : (language === 'zh' ? CATEGORY_L1_LABELS[activeL1].zh : CATEGORY_L1_LABELS[activeL1].en)}
                </h3>
                {activeL2 !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setActiveL2('all')}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20 hover:bg-primary/20 transition-colors"
                  >
                    <span>{language === 'zh' ? CATEGORY_L2_LABELS[activeL2].zh : CATEGORY_L2_LABELS[activeL2].en}</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {activeTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagClick(tag)}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20 hover:bg-primary/20 transition-colors"
                  >
                    <span>#{tag}</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ))}
                <span className="px-2.5 py-0.5 rounded-full bg-surface border border-slate-200 dark:border-white/10 text-xs font-medium text-slate-500 dark:text-slate-400 shadow-sm">
                  {totalResources} {t.items_found}
                </span>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap -mx-1 px-1 pb-1">
                {(scope === 'public' || scope === 'all') && (
                  <div
                    role="group"
                    aria-label={t.sort_by}
                    className="shrink-0 flex items-center gap-1 p-1 rounded-full bg-surface border border-slate-200 dark:border-white/10 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() => handleSortChange('latest')}
                      aria-pressed={sort === 'latest'}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        sort === 'latest'
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      {t.sort_latest}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSortChange('most_favorited')}
                      aria-pressed={sort === 'most_favorited'}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        sort === 'most_favorited'
                          ? 'bg-primary/10 text-primary'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      {t.sort_most_favorited}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setFeaturedOnly(prev => !prev)}
                  aria-pressed={featuredOnly}
                  aria-label={language === 'zh' ? '只看精选' : 'Featured only'}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    featuredOnly
                      ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                      : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5'
                  }`}
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill={featuredOnly ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.98 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.556l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                    />
                  </svg>
                  <span>{language === 'zh' ? '只看精选' : 'Featured'}</span>
                </button>
                {user && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFavoritesOnly(prev => !prev)}
                      aria-pressed={favoritesOnly}
                      aria-label={language === 'zh' ? '只看收藏' : 'Favorites only'}
                      className={`shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                        favoritesOnly
                          ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                          : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill={favoritesOnly ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                      <span>{language === 'zh' ? '只看收藏' : 'Favorites'}</span>
                    </button>
                    <div className="shrink-0">
                      <ScopeSelector
                        scope={scope}
                        onScopeChange={handleScopeChange}
                        teams={teamsForSelector}
                        selectedTeamId={selectedTeamId}
                      />
                    </div>
                  </>
                )}
              </div>

              {(activeL1 !== 'all' || activeL2 !== 'all') && (
                <div className="w-full flex items-center gap-2 overflow-x-auto whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setActiveL2('all')}
                    aria-pressed={activeL2 === 'all'}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      activeL2 === 'all'
                        ? 'bg-primary/10 text-primary border-primary/20'
                        : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                    }`}
                  >
                    {language === 'zh' ? '全部二级' : 'All L2'}
                  </button>
                  {CATEGORY_L2_VALUES.map((l2) => (
                    <button
                      key={l2}
                      type="button"
                      onClick={() => setActiveL2(l2)}
                      aria-pressed={activeL2 === l2}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        activeL2 === l2
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
                      }`}
                    >
                      {language === 'zh' ? CATEGORY_L2_LABELS[l2].zh : CATEGORY_L2_LABELS[l2].en}
                      <span className="ml-1 text-xs opacity-80">{l2Counts[l2] || 0}</span>
                    </button>
                ))}
              </div>
            )}
          </div>

          </div>

          {/* Results Grid */}
	          <div className="min-h-[400px]">
	             {isResourcesLoading ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                 {Array.from({ length: 6 }).map((_, i) => (
                   <div key={i} className="bg-surface rounded-2xl p-6 border border-slate-200 dark:border-white/5 animate-pulse">
                     <div className="flex items-start gap-4">
                       <div className="w-12 h-12 rounded-xl bg-slate-200 dark:bg-slate-700" />
                       <div className="flex-1 space-y-2">
                         <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                         <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                       </div>
                     </div>
                     <div className="mt-4 space-y-2">
                       <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded" />
                       <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-5/6" />
                     </div>
                   </div>
                 ))}
               </div>
	             ) : totalResources > 0 ? (
	               <>
	                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
	                 {currentPageResources.map((resource, index) => (
                   <ResourceCard
                     key={resource.id}
                     resource={resource}
                     index={index}
                     searchQuery={basicSearch}
                     activeTags={activeTags}
                     onTagClick={handleTagClick}
                     isFavorited={favoriteIdSet.has(resource.id)}
                     onToggleFavorite={user ? handleToggleFavorite : undefined}
                     isFavoriteActionPending={toggleFavoriteMutation.isPending}
                     onOpenRepoInsight={handleOpenRepoInsight}
                   />
                 ))}
	               </div>
	               {totalPages > 1 && (
	                 <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
	                   <p className="text-sm text-slate-500 dark:text-slate-400">
	                     {language === 'zh'
	                       ? `第 ${safeCurrentPage} / ${totalPages} 页，显示 ${pageStart}-${pageEnd} / 共 ${totalResources} 个`
	                       : `Page ${safeCurrentPage} / ${totalPages}, showing ${pageStart}-${pageEnd} of ${totalResources}`}
	                   </p>
	                   <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
	                     <button
	                       type="button"
	                       onClick={() => handlePageChange(1)}
	                       disabled={safeCurrentPage <= 1}
	                       className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-surface text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
	                     >
	                       {language === 'zh' ? '首页' : 'First'}
	                     </button>
	                     <button
	                       type="button"
	                       onClick={() => handlePageChange(safeCurrentPage - 1)}
	                       disabled={safeCurrentPage <= 1}
	                       className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-surface text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
	                     >
	                       {language === 'zh' ? '上一页' : 'Prev'}
	                     </button>
	                     {paginationButtons.map((pageNumber) => (
	                       <button
	                         key={pageNumber}
	                         type="button"
	                         onClick={() => handlePageChange(pageNumber)}
	                         aria-current={pageNumber === safeCurrentPage ? 'page' : undefined}
	                         className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
	                           pageNumber === safeCurrentPage
	                             ? 'bg-primary/10 text-primary border-primary/20'
	                             : 'bg-surface text-slate-600 dark:text-slate-300 border-slate-200 dark:border-white/10'
	                         }`}
	                       >
	                         {pageNumber}
	                       </button>
	                     ))}
	                     <button
	                       type="button"
	                       onClick={() => handlePageChange(safeCurrentPage + 1)}
	                       disabled={safeCurrentPage >= totalPages}
	                       className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-surface text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
	                     >
	                       {language === 'zh' ? '下一页' : 'Next'}
	                     </button>
	                     <button
	                       type="button"
	                       onClick={() => handlePageChange(totalPages)}
	                       disabled={safeCurrentPage >= totalPages}
	                       className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 dark:border-white/10 bg-surface text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
	                     >
	                       {language === 'zh' ? '末页' : 'Last'}
	                     </button>
	                   </div>
	                 </div>
	               )}
	               </>
	             ) : (
               <div className="flex flex-col items-center justify-center py-24 bg-surface/30 backdrop-blur-md rounded-3xl border border-dashed border-slate-300 dark:border-slate-700/50">
                 <div className="w-20 h-20 rounded-full bg-slate-200 dark:bg-slate-800/50 flex items-center justify-center mb-6 text-4xl shadow-inner text-slate-400 dark:text-inherit">
                   <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                   </svg>
                 </div>
                 <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                   {user && favoritesOnly
                     ? (language === 'zh' ? '暂无收藏' : 'No favorites yet')
                     : featuredOnly
                       ? (language === 'zh' ? '暂无精选' : 'No featured resources')
                       : t.no_results}
                 </h3>
                 <p className="text-slate-500 dark:text-slate-400 text-lg mb-8 max-w-md text-center">
                   {user && favoritesOnly && !featuredOnly
                     ? (language === 'zh' ? '点击卡片上的心形即可收藏，方便以后快速找到。' : 'Tap the heart on a card to save it for later.')
                     : featuredOnly
                       ? (language === 'zh' ? '当前筛选条件下暂无精选资源，可切换筛选或查看全部资源。' : 'No featured resources match the current filters. Try changing filters or view all.')
                       : t.try_adjusting}
                 </p>
                 {(featuredOnly || (user && favoritesOnly)) && (
                   <button
                     onClick={() => {
                       setFavoritesOnly(false);
                       setFeaturedOnly(false);
                     }}
                     className="px-6 py-2.5 bg-surface hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-white/10 rounded-xl text-slate-900 dark:text-white font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 mb-4"
                   >
                     {language === 'zh' ? '查看全部资源' : 'View all resources'}
                   </button>
                 )}
                 {basicSearch && (
                   <button 
                     onClick={() => setBasicSearch('')}
                     className="px-6 py-2.5 bg-surface hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-white/10 rounded-xl text-slate-900 dark:text-white font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                   >
                     {t.clear_ai_filter}
                   </button>
                 )}
               </div>
             )}
          </div>

        </div>

        {/* Footer */}
        <footer className="border-t border-slate-200 dark:border-white/5 mt-20 py-12 text-center transition-colors duration-300">
          <p className="text-slate-500 text-sm font-medium">© {new Date().getFullYear()} {t.footer}</p>
        </footer>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <MotionProvider>
          <LanguageProvider>
            <EffectsRuntime />
            <NoiseOverlay />
            <AppContent />
          </LanguageProvider>
        </MotionProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
